import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { orgApi, userApi } from '../../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import FormsInProgressWidget from '../../components/dashboard/FormsInProgressWidget';
import { 
  Building2, Users, ShieldCheck, Activity, AlertTriangle,
  Database, Server, CheckCircle, XCircle, ArrowRight
} from 'lucide-react';

export default function SuperAdminDashboard() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [orgs, setOrgs] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadDashboardData();
  }, []);

  const loadDashboardData = async () => {
    setLoading(true);
    try {
      const [orgsRes, usersRes] = await Promise.all([
        orgApi.list(),
        userApi.list()
      ]);
      setOrgs(orgsRes.data || []);
      setUsers(usersRes.data.users || []);
    } catch (error) {
      console.error('Failed to load dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  const systemHealth = [
    { name: 'API Server', status: 'healthy', latency: '45ms' },
    { name: 'Database', status: 'healthy', latency: '12ms' },
    { name: 'AI Service', status: 'healthy', latency: '230ms' },
    { name: 'File Storage', status: 'healthy', latency: '89ms' },
  ];

  const securityEvents = [
    { type: 'login', user: 'admin@careiq.com', time: '2 min ago', status: 'success' },
    { type: 'login', user: 'doctor@demo.careiq.com', time: '15 min ago', status: 'success' },
    { type: 'password_reset', user: 'nurse@demo.careiq.com', time: '1 hour ago', status: 'success' },
    { type: 'login_failed', user: 'unknown@test.com', time: '2 hours ago', status: 'failed' },
  ];

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-10 h-10 border-4 border-[#0E7490] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-8" data-testid="super-admin-dashboard">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-semibold text-[#0A2342]" style={{ fontFamily: 'Outfit, sans-serif' }}>
          Platform Administration
        </h1>
        <p className="text-[#64748B] mt-1">
          System overview, compliance status, and security monitoring.
        </p>
      </div>

      {/* Quick Stats - Clickable Drill-downs */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <Card className="careiq-metric-card cursor-pointer hover:shadow-lg transition-shadow" onClick={() => navigate('/admin/organizations')} data-testid="drilldown-organizations">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-[#0A2342]/10 rounded-lg flex items-center justify-center">
                <Building2 className="w-5 h-5 text-[#0A2342]" />
              </div>
              <div>
                <div className="text-2xl font-bold text-[#0A2342]">{orgs.length}</div>
                <div className="text-xs text-[#64748B]">Organizations</div>
              </div>
            </div>
            <div className="mt-2 text-xs text-[#0A2342] flex items-center gap-1">Manage orgs <ArrowRight className="w-3 h-3" /></div>
          </CardContent>
        </Card>
        
        <Card className="careiq-metric-card cursor-pointer hover:shadow-lg transition-shadow" onClick={() => navigate('/admin/settings')} data-testid="drilldown-total-users">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-[#0E7490]/10 rounded-lg flex items-center justify-center">
                <Users className="w-5 h-5 text-[#0E7490]" />
              </div>
              <div>
                <div className="text-2xl font-bold text-[#0A2342]">{users.length}</div>
                <div className="text-xs text-[#64748B]">Total Users</div>
              </div>
            </div>
            <div className="mt-2 text-xs text-[#0E7490] flex items-center gap-1">View users <ArrowRight className="w-3 h-3" /></div>
          </CardContent>
        </Card>
        
        <Card className="careiq-metric-card cursor-pointer hover:shadow-lg transition-shadow" data-testid="drilldown-baa-coverage">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-[#166534]/10 rounded-lg flex items-center justify-center">
                <ShieldCheck className="w-5 h-5 text-[#166534]" />
              </div>
              <div>
                <div className="text-2xl font-bold text-[#0A2342]">100%</div>
                <div className="text-xs text-[#64748B]">BAA Coverage</div>
              </div>
            </div>
            <div className="mt-2 text-xs text-[#166534] flex items-center gap-1">View compliance <ArrowRight className="w-3 h-3" /></div>
          </CardContent>
        </Card>
        
        <Card className="careiq-metric-card cursor-pointer hover:shadow-lg transition-shadow" data-testid="drilldown-uptime">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-[#166534]/10 rounded-lg flex items-center justify-center">
                <Activity className="w-5 h-5 text-[#166534]" />
              </div>
              <div>
                <div className="text-2xl font-bold text-[#166534]">99.9%</div>
                <div className="text-xs text-[#64748B]">Uptime</div>
              </div>
            </div>
            <div className="mt-2 text-xs text-[#166534] flex items-center gap-1">View health <ArrowRight className="w-3 h-3" /></div>
          </CardContent>
        </Card>
      </div>

      <FormsInProgressWidget />

      <div className="grid lg:grid-cols-2 gap-6">
        {/* System Health */}
        <Card className="careiq-card">
          <CardHeader>
            <CardTitle className="text-lg font-semibold text-[#0A2342] flex items-center gap-2">
              <Server className="w-5 h-5 text-[#0E7490]" />
              System Health
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {systemHealth.map((service, index) => (
                <div key={index} className="flex items-center justify-between p-3 rounded-lg bg-[#F8FAFC]">
                  <div className="flex items-center gap-3">
                    <div className={`w-3 h-3 rounded-full ${
                      service.status === 'healthy' ? 'bg-[#166534]' : 'bg-[#991B1B]'
                    }`} />
                    <span className="font-medium text-[#0A2342]">{service.name}</span>
                  </div>
                  <div className="flex items-center gap-4">
                    <span className="text-sm text-[#64748B]">{service.latency}</span>
                    <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                      service.status === 'healthy' 
                        ? 'bg-[#166534]/10 text-[#166534]' 
                        : 'bg-[#991B1B]/10 text-[#991B1B]'
                    }`}>
                      {service.status}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Security Events */}
        <Card className="careiq-card">
          <CardHeader>
            <CardTitle className="text-lg font-semibold text-[#0A2342] flex items-center gap-2">
              <ShieldCheck className="w-5 h-5 text-[#0E7490]" />
              Recent Security Events
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {securityEvents.map((event, index) => (
                <div key={index} className="flex items-center justify-between p-3 rounded-lg bg-[#F8FAFC]">
                  <div className="flex items-center gap-3">
                    {event.status === 'success' ? (
                      <CheckCircle className="w-4 h-4 text-[#166534]" />
                    ) : (
                      <XCircle className="w-4 h-4 text-[#991B1B]" />
                    )}
                    <div>
                      <div className="font-medium text-[#0A2342]">{event.type.replace('_', ' ')}</div>
                      <div className="text-xs text-[#64748B]">{event.user}</div>
                    </div>
                  </div>
                  <span className="text-xs text-[#64748B]">{event.time}</span>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Organizations */}
      <Card className="careiq-card">
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle className="text-lg font-semibold text-[#0A2342]">Organizations</CardTitle>
          <Button className="careiq-btn-secondary" data-testid="add-organization-btn">
            Add Organization
          </Button>
        </CardHeader>
        <CardContent>
          {orgs.length > 0 ? (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-[#E2E8F0]">
                    <th className="table-header text-left">Organization</th>
                    <th className="table-header text-left">Address</th>
                    <th className="table-header text-left">Phone</th>
                    <th className="table-header text-left">Created</th>
                  </tr>
                </thead>
                <tbody>
                  {orgs.map((org) => (
                    <tr key={org.id} className="table-row">
                      <td className="table-cell font-medium">{org.name}</td>
                      <td className="table-cell text-[#64748B]">{org.address || '-'}</td>
                      <td className="table-cell text-[#64748B]">{org.phone || '-'}</td>
                      <td className="table-cell text-[#64748B]">
                        {new Date(org.created_at).toLocaleDateString()}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="text-center py-8 text-[#64748B]">
              <Building2 className="w-12 h-12 mx-auto mb-3 text-[#94A3B8]" />
              <p>No organizations yet</p>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
